import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ProviderForm from './ProviderForm';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { providerAPI, providerTypeAPI } from '../services/api';
import PageHeader from './PageHeader';
import DeleteIconButton from './DeleteIconButton';
import { hasPermission } from '../utils/permissions';
import './ListWithDetail.css';
import './ProviderList.css';

const PAGE_SIZE = 25;

function ProviderList() {
  const navigate = useNavigate();
  const { id: detailId } = useParams();
  const { tenantPath } = useTenantFromUrl();
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [providerSearch, setProviderSearch] = useState(searchParams.get('q') || '');
  const [payeeSearch, setPayeeSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [types, setTypes] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const sentinelRef = useRef(null);
  /** Always-current filters for stale-response checks after await (closure would be stale). */
  const filtersRef = useRef({ providerSearch, payeeSearch, typeFilter, includeInactive });
  filtersRef.current = { providerSearch, payeeSearch, typeFilter, includeInactive };
  /** Latest page-1 list request; older page-1 responses are ignored (fixes out-of-order API when typing). */
  const page1RequestIdRef = useRef(0);

  const canCreateProvider = hasPermission('providers.create');
  const canDeleteProvider = hasPermission('providers.delete');

  useEffect(() => {
    providerTypeAPI.getAll().then((r) => setTypes(r.data?.data ?? r.data ?? [])).catch(() => setTypes([]));
  }, []);

  const loadProviders = useCallback(async (pageNum = 1, append = false) => {
    const snap = { ...filtersRef.current };
    let page1Id;
    if (pageNum === 1 && !append) {
      page1Id = ++page1RequestIdRef.current;
    }
    try {
      if (pageNum === 1 && !append) setLoading(true);
      else setLoadingMore(true);
      const params = { page: pageNum, pageSize };
      if (snap.providerSearch.trim()) params.q = snap.providerSearch.trim();
      if (snap.payeeSearch.trim()) params.payee_q = snap.payeeSearch.trim();
      if (snap.typeFilter) params.provider_type_id = snap.typeFilter;
      if (snap.includeInactive) params.includeInactive = 'true';
      const response = await providerAPI.getAll(params);
      if (pageNum === 1 && !append) {
        if (page1Id !== page1RequestIdRef.current) return;
      } else if (append) {
        const cur = filtersRef.current;
        if (
          snap.providerSearch !== cur.providerSearch ||
          snap.payeeSearch !== cur.payeeSearch ||
          snap.typeFilter !== cur.typeFilter ||
          snap.includeInactive !== cur.includeInactive
        ) {
          return;
        }
      }
      const data = response.data.data ?? [];
      const newTotal = response.data.total ?? 0;
      if (append) setProviders((prev) => [...prev, ...data]);
      else setProviders(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (error) {
      if (pageNum === 1 && !append && page1Id !== page1RequestIdRef.current) return;
      if (append) {
        const cur = filtersRef.current;
        if (
          snap.providerSearch !== cur.providerSearch ||
          snap.payeeSearch !== cur.payeeSearch ||
          snap.typeFilter !== cur.typeFilter
        ) {
          return;
        }
      }
      console.error('Error loading providers:', error);
      alert('Failed to load providers');
    } finally {
      if (pageNum === 1 && !append) {
        if (page1Id === page1RequestIdRef.current) {
          setLoading(false);
        }
      } else {
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  useDebouncedSearch(`${providerSearch}|${payeeSearch}|${typeFilter}|${includeInactive}`, () => loadProviders(1));

  const hasMore = providers.length < total && total > 0;
  const loadMore = () => { if (!loading && !loadingMore && hasMore) loadProviders(page + 1, true); };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    try {
      const res = await providerAPI.delete(id);
      const { action, references } = res.data;
      if (action === 'deactivated') {
        alert(
          `This provider has active records (${references.join('; ')}) and cannot be fully deleted. ` +
          `They have been deactivated and will no longer appear in search or dropdowns.`
        );
        // Update the row in-place to show as inactive (if includeInactive is on), or remove it
        if (includeInactive) {
          setProviders((prev) => prev.map((p) => p.id === id ? { ...p, is_active: false } : p));
        } else {
          setProviders((prev) => prev.filter((p) => p.id !== id));
          setTotal((t) => Math.max(0, t - 1));
        }
      } else {
        if (String(id) === String(detailId)) navigate(tenantPath('/lists/providers'));
        setProviders((prev) => prev.filter((p) => p.id !== id));
        setTotal((t) => Math.max(0, t - 1));
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert(error.response?.data?.error || 'Failed to delete provider');
    }
  };

  const handleReactivate = async (id, e) => {
    e.stopPropagation();
    try {
      await providerAPI.reactivate(id);
      setProviders((prev) => prev.map((p) => p.id === id ? { ...p, is_active: true } : p));
    } catch (error) {
      console.error('Error reactivating provider:', error);
      alert('Failed to reactivate provider');
    }
  };

  const colCount = 3 + (canDeleteProvider ? 1 : 0);
  const showPanel = detailId != null;

  return (
    <div className={`list-with-detail ${showPanel ? 'list-with-detail--panel-open' : ''}`}>
    <div className="provider-list list-with-detail__list">
      <div className="header">
        <PageHeader title="Providers">
          {canCreateProvider && (
            <button onClick={() => navigate(tenantPath('/lists/providers/new'))} className="btn-new">
              + New Provider
            </button>
          )}
        </PageHeader>
      </div>

      <div className="provider-list-top-bar">
        <div className="provider-list-pagination-info">
          {loading && providers.length === 0
            ? 'Loading...'
            : total === 0
              ? 'No providers found'
              : total === 1
                ? '1 provider found'
                : `${total} providers found`}
        </div>
        <label className="provider-list-inactive-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include inactive
        </label>
      </div>

      <div className="provider-list-table-wrap list-table-wrap">
        <table className="provider-list-table list-table">
          <thead>
            <tr className="list-header-labels">
              <th>Provider</th>
              <th>Payee</th>
              <th>Type</th>
              {canDeleteProvider && <th>Delete</th>}
            </tr>
            <tr className="list-filter-row provider-list-filter-row">
              <th>
                <input
                  type="text"
                  className="provider-list-filter-input"
                  placeholder="Search provider..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  aria-label="Filter by provider name"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="provider-list-filter-input"
                  placeholder="Search payee..."
                  value={payeeSearch}
                  onChange={(e) => setPayeeSearch(e.target.value)}
                  aria-label="Filter by payee name"
                />
              </th>
              <th>
                <select
                  className="list-header-filter-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by type"
                >
                  <option value="">All</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name || t.code || t.id}</option>
                  ))}
                </select>
              </th>
              {canDeleteProvider && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && providers.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data loading-cell">Loading providers...</td>
              </tr>
            ) : providers.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data">No providers found</td>
              </tr>
            ) : (
              providers.map((provider) => (
                <tr
                  key={provider.id}
                  onClick={() => navigate(tenantPath(`/lists/providers/${provider.id}`))}
                  className={`clickable-row${provider.is_active === false ? ' provider-row-inactive' : ''}`}
                >
                  <td>
                    {[provider.last_name, provider.first_name].filter(Boolean).join(', ') || '—'}
                    {provider.is_active === false && <span className="provider-inactive-badge">Inactive</span>}
                  </td>
                  <td>{provider.payee_name || 'N/A'}</td>
                  <td>{provider.provider_type_code || 'N/A'}</td>
                  {canDeleteProvider && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {provider.is_active === false ? (
                        <button
                          className="provider-reactivate-btn"
                          onClick={(e) => handleReactivate(provider.id, e)}
                          title="Reactivate provider"
                        >
                          Reactivate
                        </button>
                      ) : (
                        <DeleteIconButton
                          className="list-btn-delete"
                          onClick={() => handleDelete(provider.id)}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>

    {showPanel && (
      <div className="list-with-detail__panel">
        <ProviderForm />
      </div>
    )}
    </div>
  );
}

export default ProviderList;
